import { formatDate, useApiErrorMessage, useTranslation } from "@roastery/i18n";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  PageHeader,
  toast,
  useConfirm,
} from "@roastery/ui";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { PauseIcon, PlayIcon, ShoppingCartIcon, SlidersHorizontalIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { StandingOrderDialog } from "../components/standing-order-dialog";
import {
  type StandingOrder,
  useDeletePortalStandingOrder,
  usePausePortalStandingOrder,
  usePortalIdentity,
  usePortalStandingOrders,
} from "../lib/portal-api";
import { CreditBlockNotice } from "../components/credit-block-notice";
import { useReorder } from "../lib/use-reorder";
import { useShelfLines } from "../lib/use-shelf-lines";
import { ShelfLineLabel } from "../components/shelf-line-label";
import { usePortalFormatSettings } from "../lib/use-portal-format";

export const Route = createFileRoute("/_authed/standing-orders")({
  component: StandingOrdersPage,
});

/**
 * The customer's standing instructions (US-10.6).
 *
 * <p>The screen answers the two questions a cafe actually has: when is the next one coming, and what
 * is on it. Everything else is one click — pause for August, change the schedule, drop it into the
 * cart to order early. There are no prices here, because an instruction has none: it is priced on
 * the morning it goes in.
 */
function StandingOrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = usePortalFormatSettings();
  const standing = usePortalStandingOrders();
  // While the account is blocked the morning job skips the instruction and e-mails the buyer
  // (US-25.3); saying so beside it is the difference between "it did not come" and "we told you".
  const creditBlock = usePortalIdentity().data?.creditBlock;
  const lookUp = useShelfLines();
  const reorder = useReorder();
  const pause = usePausePortalStandingOrder();
  const remove = useDeletePortalStandingOrder();
  const errorMessage = useApiErrorMessage();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<StandingOrder | undefined>(undefined);

  const onDelete = async (instruction: StandingOrder) => {
    const confirmed = await confirm({
      title: t("portal.standing.confirmDelete"),
      description: t("portal.standing.confirmDeleteBody", { name: instruction.name }),
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      destructive: true,
    });
    if (!confirmed) {
      return;
    }
    remove.mutate(
      { params: { path: { standingOrderId: instruction.id ?? "" } } },
      {
        onSuccess: () => toast.success(t("portal.standing.deleted", { name: instruction.name })),
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  if (standing.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="portal-standing-loading">
        {t("app.loading")}
      </p>
    );
  }

  const rows = standing.data ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="portal-standing-orders">
      <div>
        <PageHeader title={t("portal.standing.title")} />
        <p className="text-sm text-muted-foreground">{t("portal.standing.description")}</p>
      </div>

      {rows.length === 0 ? (
        <Card data-testid="portal-standing-empty">
          <CardHeader>
            <CardDescription>{t("portal.standing.empty")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button data-testid="portal-back-to-catalog" onClick={() => void navigate({ to: "/" })}>
              {t("portal.cart.placed.backToCatalog")}
            </Button>
          </CardFooter>
        </Card>
      ) : (
        rows.map((instruction) => {
          const lines = instruction.lines ?? [];
          const resolved = lookUp(lines);
          const onShelf = new Map(resolved.lines.map((line) => [line.variantId, line]));
          return (
            <Card key={instruction.id} data-testid={`portal-standing-${instruction.id}`}>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  {instruction.name}
                  {instruction.paused ? (
                    <Badge variant="muted" data-testid={`portal-standing-paused-${instruction.id}`}>
                      {t("portal.standing.paused")}
                    </Badge>
                  ) : (
                    <Badge variant="success">{t("portal.standing.active")}</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {t("portal.standing.schedule", {
                    count: instruction.everyWeeks ?? 1,
                    weekday: t(`portal.weekdayOn.${instruction.weekday ?? "MONDAY"}`),
                  })}
                  {" · "}
                  {t("portal.standing.next", {
                    delivery: instruction.nextDeliveryOn
                      ? formatDate(instruction.nextDeliveryOn, settings)
                      : "—",
                    placed: instruction.nextPlacedOn ? formatDate(instruction.nextPlacedOn, settings) : "—",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {creditBlock && !instruction.paused ? (
                  <CreditBlockNotice
                    block={creditBlock}
                    className="mb-3"
                    testId={`portal-standing-credit-block-${instruction.id}`}
                  >
                    {t("portal.standing.creditBlocked")}
                  </CreditBlockNotice>
                ) : null}
                <ul className="flex flex-col gap-1 text-sm">
                  {lines.map((line) => (
                    <li
                      key={line.variantId}
                      className="flex items-center justify-between gap-2"
                      data-testid={`portal-standing-line-${line.sku}`}
                    >
                      <ShelfLineLabel line={line} onShelf={line.variantId ? onShelf.get(line.variantId) : undefined} />
                      {line.variantId && onShelf.has(line.variantId) ? null : (
                        <Badge variant="warning" data-testid={`portal-standing-withdrawn-${line.sku}`}>
                          {t("portal.templates.withdrawn")}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-muted-foreground">
                  {t("portal.standing.pricedOnTheDay")}
                  {instruction.lastOrderNumber ? (
                    <>
                      {" · "}
                      {t("portal.standing.lastOrder")}{" "}
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: instruction.lastOrderId ?? "" }}
                        className="underline underline-offset-2"
                        data-testid={`portal-standing-last-${instruction.id}`}
                      >
                        {instruction.lastOrderNumber}
                      </Link>
                    </>
                  ) : null}
                </p>
              </CardContent>
              <CardFooter className="flex-wrap gap-2">
                <Button
                  variant="outline"
                  data-testid={`portal-standing-edit-${instruction.id}`}
                  onClick={() => setEditing(instruction)}
                >
                  <SlidersHorizontalIcon aria-hidden />
                  {t("portal.standing.editSchedule")}
                </Button>
                <Button
                  variant="outline"
                  data-testid={`portal-standing-pause-${instruction.id}`}
                  onClick={() =>
                    pause.mutate(
                      {
                        params: { path: { standingOrderId: instruction.id ?? "" } },
                        body: { paused: !instruction.paused },
                      },
                      {
                        onSuccess: (saved) =>
                          toast.success(
                            saved.paused
                              ? t("portal.standing.pausedToast", { name: saved.name })
                              : t("portal.standing.resumedToast", { name: saved.name }),
                          ),
                        onError: (error) => toast.error(errorMessage(error)),
                      },
                    )
                  }
                >
                  {instruction.paused ? <PlayIcon aria-hidden /> : <PauseIcon aria-hidden />}
                  {instruction.paused ? t("portal.standing.resume") : t("portal.standing.pause")}
                </Button>
                <Button
                  variant="ghost"
                  data-testid={`portal-standing-cart-${instruction.id}`}
                  disabled={!resolved.ready}
                  onClick={() => reorder(lines)}
                >
                  <ShoppingCartIcon aria-hidden />
                  {t("portal.templates.addToCart")}
                </Button>
                <Button
                  variant="ghost"
                  data-testid={`portal-standing-delete-${instruction.id}`}
                  onClick={() => void onDelete(instruction)}
                >
                  <Trash2Icon aria-hidden />
                  {t("portal.standing.delete")}
                </Button>
              </CardFooter>
            </Card>
          );
        })
      )}

      {editing ? (
        <StandingOrderDialog key={editing.id} open existing={editing} onClose={() => setEditing(undefined)} />
      ) : null}
    </div>
  );
}
